import { Mic } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useVoice } from '../../context/VoiceContext';

/**
 * Bloque informativo oficial de "Comunicacion verbal con DamyxIA".
 * Texto literal segun brief — NO modificar.
 *
 * Reglas:
 *  - Se renderiza SOLO si el usuario NO esta en una sesion de voz activa
 *    (voiceActive === false) para no saturar la UI durante una conversacion.
 *  - EN/ES segun LanguageContext (user.language).
 *  - Prop blocked (opcional): si true, muestra CTA a Premium (para Free users).
 *  - Prop hideWhenActive (default true) permite apagar el ocultamiento en
 *    ubicaciones donde el bloque es pura referencia (tabla de Planes, etc).
 */
const TEXT = {
  es: {
    title: 'Comunicación verbal con DamyxIA',
    line1: 'Hablá naturalmente y DamyxIA convertirá tu voz en texto para ayudarte más rápido.',
    line2: 'DamyxIA responde por escrito y ejecuta tus instrucciones.',
    line3: 'La comunicación verbal actual es: voz → texto → respuesta escrita.',
    line4: 'Exclusivo para usuarios Premium.',
    cta: 'Actualizar a Premium',
  },
  en: {
    title: 'Voice Interaction with DamyxIA',
    line1: 'Speak naturally and DamyxIA will convert your voice into text to help you faster.',
    line2: 'DamyxIA replies in text and executes your instructions.',
    line3: 'Current voice interaction is: voice → text → written response.',
    line4: 'Premium feature.',
    cta: 'Upgrade to Premium',
  },
};

export default function VoiceInfoBlock({
  blocked = false,
  onUpgrade,
  hideWhenActive = true,
  className = '',
  languageOverride,
}) {
  const { language } = useLanguage();
  const { voiceActive } = useVoice();
  const effectiveLang = languageOverride === 'en' || languageOverride === 'es' ? languageOverride : language;
  const t = effectiveLang === 'en' ? TEXT.en : TEXT.es;

  if (hideWhenActive && voiceActive) return null;

  return (
    <div
      className={`flex gap-4 items-start rounded-xl p-6 border border-zinc-800 ${className}`}
      style={{ backgroundColor: '#0D0D0D' }}
      data-testid="voice-info-block"
      data-lang={effectiveLang === 'en' ? 'en' : 'es'}
    >
      <div
        className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center border"
        style={{
          backgroundColor: blocked ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
          borderColor: blocked ? 'rgba(245, 158, 11, 0.3)' : 'rgba(16, 185, 129, 0.3)',
        }}
        aria-hidden="true"
      >
        <Mic
          className="w-5 h-5"
          style={{ color: blocked ? '#f59e0b' : '#10b981' }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3
          className="text-base font-semibold mb-2"
          style={{ color: '#FFFFFF' }}
          data-testid="voice-info-block-title"
        >
          🎤 {t.title}
        </h3>
        <p
          className="text-sm leading-relaxed"
          style={{ color: '#B3B3B3' }}
          data-testid="voice-info-block-body"
        >
          {t.line1}
          <br />
          {t.line2}
          <br />
          {t.line3}
          <br />
          <span
            className="inline-block mt-1 text-xs font-semibold"
            style={{ color: blocked ? '#f59e0b' : '#10b981' }}
          >
            {t.line4}
          </span>
        </p>
        {blocked && onUpgrade && (
          <button
            type="button"
            onClick={onUpgrade}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg
                       text-sm font-semibold bg-amber-500 hover:bg-amber-400
                       text-zinc-900 shadow-lg shadow-amber-500/30 transition-colors"
            data-testid="voice-info-block-cta"
          >
            {t.cta}
          </button>
        )}
      </div>
    </div>
  );
}
